import React, { useState, useEffect } from 'react';
import { Project, ProjectStatus, ProjectPriority, canModifyProject, ViewMode } from '../types';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import ContextMenu, { ContextMenuItem } from './ContextMenu';
import { getCurrencySymbol } from '../utils/currency';
import { apiGet, apiPost } from '../utils/api';
import { API_CONFIG, SSO_CONFIG } from '../config';

interface ProjectListProps {
  projects: Project[];
  currentRole: string | null;
  currentUserId?: string;
  onEditProject: (project: Project) => void;
  onUpdateProject?: (project: Project) => void;
  onDeleteProject: (projectId: string) => void;
  onCreateBidding?: (project: Project, mode: 'video' | 'manual') => void;
  onCreateProject: () => void;
  onAddUsersToProject?: (project: Project) => void;
  viewMode: ViewMode;
}

// Helper: Check if user can view sensitive data
const canViewSensitive = (role: string | null): boolean => {
  if (!role) return false;
  return role === 'Super Admin' || role === 'Producer';
};

// Format date helper
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

// Helper: Convert status to translation key
const getStatusTranslationKey = (status: ProjectStatus): string => {
  const statusMap: Record<ProjectStatus, string> = {
    'Planning': 'planning',
    'In Progress': 'inProgress',
    'On Hold': 'onHold',
    'Completed': 'completed',
    'Cancelled': 'cancelled',
    'MagicBox': 'magicbox',
  };
  return statusMap[status] || status.toLowerCase();
};

// Helper: Convert priority to translation key
const getPriorityTranslationKey = (priority: ProjectPriority): string => {
  return priority.toLowerCase();
};

// Status badge colors
const getStatusColor = (status?: ProjectStatus): string => {
  switch (status) {
    case 'In Progress': return 'bg-blue-500';
    case 'Completed': return 'bg-indigo-500';
    case 'On Hold': return 'bg-yellow-500';
    case 'Cancelled': return 'bg-red-500';
    case 'MagicBox': return 'bg-purple-500';
    default: return 'bg-gray-500';
  }
};

// Priority badge colors
const getPriorityColor = (priority?: ProjectPriority): string => {
  switch (priority) {
    case 'Urgent': return 'bg-red-600';
    case 'High': return 'bg-orange-500';
    case 'Medium': return 'bg-blue-500';
    case 'Low': return 'bg-gray-500';
    default: return 'bg-gray-500';
  }
};

const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  currentRole,
  currentUserId,
  onEditProject,
  onUpdateProject,
  onDeleteProject,
  onCreateBidding,
  onCreateProject,
  onAddUsersToProject,
  viewMode
}) => {
  const { t } = useTranslation();
  const canEditSensitive = canViewSensitive(currentRole);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; project: Project } | null>(null);
  const [generalContextMenu, setGeneralContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [infoModal, setInfoModal] = useState<{ message: string } | null>(null);

  // Close context menu on click
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
      setGeneralContextMenu(null);
    };
    if (contextMenu || generalContextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu, generalContextMenu]);

  const handleContextMenu = (e: React.MouseEvent, project: Project) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, project });
    setGeneralContextMenu(null);
  };

  const handleGeneralContextMenu = (e: React.MouseEvent) => {
    // Only show if right-click on background, not on tiles
    if ((e.target as HTMLElement).closest('[data-project-tile]')) {
      return;
    }
    e.preventDefault();
    setGeneralContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleTileClick = (project: Project) => {
    if (!canModifyProject(project, currentUserId, currentRole)) return;
    onEditProject(project);
  };

  // Open project in MagicBox (context menu)
  const handleOpenInMagicBox = async (project: Project) => {
    const magicboxUrl = SSO_CONFIG.MAGICBOX_URL;
    console.log('🔗 handleOpenInMagicBox:', { projectId: project.id, projectName: project.name, magicboxUrl });
    
    if (!magicboxUrl) {
      setInfoModal({ message: t('contextMenu.magicboxNotConfigured') || 'LightBox is not configured' });
      return;
    }
    
    try {
      // First check if project is connected to MagicBox
      console.log('📡 Fetching project link...');
      const linkResponse = await apiGet(`${API_CONFIG.API_URL}/magicbox/project-link/${project.id}`);
      const linkData = await linkResponse.json();
      console.log('📦 Project link response:', linkData);
      
      if (!linkData.success) {
        // Project is not connected to MagicBox
        console.log('❌ Project not connected to MagicBox');
        setInfoModal({
          message: t('contextMenu.projectNotInMagicBox', { name: project.name }) || 
            `Project "${project.name}" is not connected to LightBox`
        });
        return;
      }
      
      // Get cross-auth token
      console.log('🔑 Getting cross-auth token...');
      const tokenResponse = await apiPost(`${API_CONFIG.API_URL}/auth/cross-token`, { targetSystem: 'magicbox' });
      const tokenData = await tokenResponse.json();
      console.log('🎫 Got cross-auth token');
      
      // Redirect to MagicBox with cross-auth token and MagicBox project ID (deep link)
      const url = new URL('/cross-login', magicboxUrl);
      url.searchParams.set('token', tokenData.crossToken);
      url.searchParams.set('projectId', linkData.magicboxProjectId);
      console.log('🚀 Redirecting to:', url.href);
      window.location.href = url.href;
    } catch (error: any) {
      console.error('❌ Error opening project in MagicBox:', error);
      setInfoModal({ message: error.message || t('contextMenu.magicboxError') || 'Failed to open in LightBox' });
    }
    setContextMenu(null);
  };

  // Handle sync progress from MagicBox
  const handleSyncFromMagicBox = async (project: Project) => {
    console.log('🔄 Syncing progress from MagicBox for:', project.name);
    setContextMenu(null);
    
    try {
      const response = await apiPost(`${API_CONFIG.API_URL}/magicbox/sync-progress/${project.id}`, {});
      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Progress synced:', data);
        setInfoModal({ 
          message: `✅ Прогресс синхронизирован: ${data.progress}% (${data.completedShots}/${data.totalShots} шотов)` 
        });
        // Trigger project list refresh
        if (onUpdateProject) {
          onUpdateProject({
            ...project,
            magicboxProgress: data.progress,
            magicboxCompletedShots: data.completedShots,
            magicboxTotalShots: data.totalShots
          });
        }
      } else {
        setInfoModal({ message: `❌ ${data.error || 'Ошибка синхронизации'}` });
      }
    } catch (error: any) {
      console.error('❌ Error syncing from MagicBox:', error);
      setInfoModal({ message: `❌ Ошибка: ${error.message || 'Не удалось синхронизировать'}` });
    }
  };

  const getContextMenuItems = (project: Project): ContextMenuItem[] => {
    const canEdit = canModifyProject(project, currentUserId, currentRole);
    // Admin and Producer have full access to all menu items
    const hasFullAccess = currentRole === 'Admin' || currentRole === 'Producer' || currentRole === 'Super Admin';

    return [
      {
        label: t('contextMenu.edit'),
        icon: <ICONS.Edit className="w-4 h-4" />,
        onClick: () => onEditProject(project),
        disabled: !canEdit,
      },
      {
        separator: true,
        label: '',
        onClick: () => {},
      },
      {
        label: t('contextMenu.createBiddingFromVideo'),
        icon: <ICONS.Video className="w-4 h-4" />,
        onClick: () => onCreateBidding?.(project, 'video'),
        disabled: hasFullAccess ? false : !onCreateBidding,
      },
      {
        label: t('contextMenu.createBiddingManual'),
        icon: <ICONS.Calculator className="w-4 h-4" />,
        onClick: () => onCreateBidding?.(project, 'manual'),
        disabled: hasFullAccess ? false : !onCreateBidding,
      },
      {
        separator: true,
        label: '',
        onClick: () => {},
      },
      {
        label: t('contextMenu.addUsersToProject') || 'Add Users to Project',
        icon: <ICONS.UserPlus className="w-4 h-4" />,
        onClick: () => {
          onAddUsersToProject?.(project);
        },
        disabled: hasFullAccess ? false : (!onAddUsersToProject || !canEdit),
      },
      {
        separator: true,
        label: '',
        onClick: () => {},
      },
      {
        label: t('contextMenu.openInMagicBox'),
        icon: <ICONS.Link className="w-4 h-4" />,
        onClick: () => handleOpenInMagicBox(project),
        disabled: !SSO_CONFIG.MAGICBOX_URL, // Disabled if MagicBox URL not configured
      },
      {
        label: t('contextMenu.syncFromMagicBox') || 'Sync from LightBox',
        icon: <ICONS.Refresh className="w-4 h-4" />,
        onClick: () => handleSyncFromMagicBox(project),
        disabled: !SSO_CONFIG.MAGICBOX_URL || !project.magicboxProjectId,
      },
    ];
  };

  if (projects.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <ICONS.Briefcase className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p className="text-xl">{t('projectsList.noProjects')}</p>
        <p className="text-sm mt-2">{t('projectsList.createFirst')}</p>
      </div>
    );
  }
  
  // List view
  if (viewMode === ViewMode.LIST) {
    return (
      <>
        <div className="flex flex-col space-y-2" onContextMenu={handleGeneralContextMenu}>
          {projects.map((project) => {
            const canEdit = canModifyProject(project, currentUserId, currentRole);
            return (
            <div
              key={project.id}
              data-project-tile="true"
              className={`bg-gray-800/50 border border-gray-700 rounded-lg p-4 hover:border-indigo-500/50 transition-all flex items-center gap-4 ${
                canEdit ? 'cursor-pointer' : 'cursor-default'
              }`}
              style={{ borderLeft: `4px solid ${project.color || '#6366f1'}` }}
              onClick={() => handleTileClick(project)}
              onContextMenu={(e) => handleContextMenu(e, project)}
            >
              {/* Image */}
              {project.imageUrl ? (
                <img 
                  src={project.imageUrl}
                  alt={project.name}
                  className="w-16 h-16 object-cover rounded"
                />
              ) : (
                <div className="w-16 h-16 bg-gray-900 rounded flex items-center justify-center">
                  <ICONS.Briefcase className="w-6 h-6 text-gray-600" />
                </div>
              )}
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-white truncate">{project.name}</h3>
                  {project.magicboxProjectId && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-600 text-white rounded" title={t('projectsList.linkedToMagicBox')}>
                      LB
                    </span>
                  )}
                </div>
                <div className="flex gap-4 text-sm text-gray-400 mt-1">
                  <span>{formatDate(project.startDate)} — {formatDate(project.endDate)}</span>
                  {project.status && (
                    <span className="flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${getStatusColor(project.status)}`}></span>
                      {t(`projectStatus.${getStatusTranslationKey(project.status)}`)}
                    </span>
                  )}
                  {project.clientName && (
                    <span className="flex items-center gap-1">
                      👤 {project.clientName}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Progress - MagicBox или ручной */}
              {project.magicboxProjectId ? (
                <div className="flex items-center gap-2 mx-4">
                  <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 transition-all duration-300"
                      style={{ width: `${project.magicboxProgress ?? 0}%` }}
                    />
                  </div>
                  <span className="text-xs text-purple-400 font-medium whitespace-nowrap">
                    {project.magicboxProgress ?? 0}%
                    {project.magicboxCompletedShots !== null && project.magicboxTotalShots !== null && (
                      <span className="text-gray-500 ml-1">({project.magicboxCompletedShots}/{project.magicboxTotalShots})</span>
                    )}
                  </span>
                </div>
              ) : typeof project.progress === 'number' && project.progress >= 0 ? (
                <div className="flex items-center gap-2 mx-4">
                  <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 transition-all duration-300"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                  <span className="text-xs text-green-400 font-medium whitespace-nowrap">
                    {project.progress}%
                  </span>
                </div>
              ) : null}
              
              {/* Budget */}
              {canEditSensitive && project.budget && (
                <div className="text-right">
                  <div className="text-lg font-bold text-indigo-400">
                    {getCurrencySymbol(project.currency)}{project.budget.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
        
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={getContextMenuItems(contextMenu.project)}
            onClose={() => setContextMenu(null)}
          />
        )}
        
        {generalContextMenu && (
          <ContextMenu
            x={generalContextMenu.x}
            y={generalContextMenu.y}
            items={[
              {
                label: t('contextMenu.createProject'),
                icon: <ICONS.Plus className="w-4 h-4" />,
                onClick: onCreateProject,
              },
            ]}
            onClose={() => setGeneralContextMenu(null)}
          />
        )}
      </>
    );
  }

  // Grid view
  return (
    <>
      <div 
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4"
        onContextMenu={handleGeneralContextMenu}
      >
        {projects.map((project) => {
          const canEdit = canModifyProject(project, currentUserId, currentRole);
          return (
          <div
            key={project.id}
            data-project-tile="true"
            className={`bg-gray-800 rounded-lg shadow-lg hover:shadow-xl transition-all overflow-hidden ${
              canEdit ? 'cursor-pointer active:scale-95 active:shadow-inner' : 'cursor-default'
            }`}
            style={{ borderTop: `4px solid ${project.color || '#6366f1'}` }}
            onClick={() => handleTileClick(project)}
            onContextMenu={(e) => handleContextMenu(e, project)}
          >
          {/* Project Image */}
          <div className="w-full h-32 bg-gray-900 overflow-hidden flex items-center justify-center relative">
            {project.imageUrl ? (
              <img 
                src={`/uploads/projects/${project.imageUrl}`} 
                alt={project.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <ICONS.Briefcase className="w-12 h-12 text-gray-600" />
            )}
            {/* MagicBox Badge */}
            {project.magicboxProjectId && (
              <div 
                className="absolute top-2 right-2 px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded shadow-lg flex items-center gap-1"
                title={t('projectsList.linkedToMagicBox')}
              >
                <ICONS.Link className="w-3 h-3" />
                <span>LB</span>
              </div>
            )}
          </div>
          
          <div className="p-4">
            {/* Header */}
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-white text-lg mb-1 line-clamp-2">
                  {project.name}
                </h3>
                {project.client && (
                  <p className="text-sm text-gray-400">
                    🏢 {project.client}
                  </p>
                )}
                {project.createdByUsername && (
                  <p className="text-xs text-gray-500 mt-1">
                    👤 {project.createdByUsername}
                  </p>
                )}
              </div>
              
              {/* Actions */}
              {canModifyProject(project, currentUserId, currentRole) && (
                <div className="flex space-x-1 ml-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProject(project.id);
                    }}
                    className="text-red-400 hover:text-red-300 p-1.5 hover:bg-gray-700 rounded transition-colors"
                    title={t('common.delete')}
                  >
                    <ICONS.Trash className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Description */}
            {project.description && (
              <p className="text-sm text-gray-300 mb-3 line-clamp-2">
                {project.description}
              </p>
            )}

            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-3">
              {project.status && (
                <span className={`px-2 py-1 text-xs font-semibold text-white rounded ${getStatusColor(project.status)}`}>
                  {t(`projectStatus.${getStatusTranslationKey(project.status)}`)}
                </span>
              )}
              {project.priority && (
                <span className={`px-2 py-1 text-xs font-semibold text-white rounded ${getPriorityColor(project.priority)}`}>
                  {t(`priority.${getPriorityTranslationKey(project.priority)}`)}
                </span>
              )}
              {project.projectType && (
                <span className="px-2 py-1 text-xs font-semibold text-white bg-purple-600 rounded">
                  {project.projectType}
                </span>
              )}
            </div>

            {/* Tags */}
            {project.tags && project.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {project.tags.slice(0, 3).map((tag, idx) => (
                  <span key={idx} className="px-2 py-0.5 text-xs text-gray-300 bg-gray-700 rounded">
                    #{tag}
                  </span>
                ))}
                {project.tags.length > 3 && (
                  <span className="px-2 py-0.5 text-xs text-gray-400">
                    +{project.tags.length - 3}
                  </span>
                )}
              </div>
            )}

            {/* Dates */}
            <div className="text-xs text-gray-400 mb-3">
              📅 {formatDate(project.startDate)} - {formatDate(project.endDate)}
            </div>

            {/* Team */}
            {(project.producerName || project.managerName || project.coordinatorName || project.clientName) && (
              <div className="text-xs text-gray-400 mb-3 space-y-1">
                {project.producerName && (
                  <div>🎬 Producer: {project.producerName}</div>
                )}
                {project.managerName && (
                  <div>📋 PM/Supervisor: {project.managerName}</div>
                )}
                {project.coordinatorName && (
                  <div>🎯 Coordinator: {project.coordinatorName}</div>
                )}
                {project.clientName && (
                  <div>👤 Client: {project.clientName}</div>
                )}
              </div>
            )}

            {/* Progress - показываем MagicBox прогресс если связан, иначе ручной */}
            {project.magicboxProjectId ? (
              // MagicBox progress (purple)
              <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span className="flex items-center gap-1">
                    <span className="text-purple-400 text-[10px] font-bold">LB</span>
                    {t('projectsList.progress')}
                  </span>
                  <span className="text-purple-400">
                    {project.magicboxProgress ?? 0}%
                    {project.magicboxCompletedShots !== null && project.magicboxTotalShots !== null && (
                      <span className="text-gray-500 ml-1">({project.magicboxCompletedShots}/{project.magicboxTotalShots})</span>
                    )}
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all"
                    style={{ width: `${project.magicboxProgress ?? 0}%` }}
                  />
                </div>
              </div>
            ) : typeof project.progress === 'number' && project.progress >= 0 ? (
              // Manual progress (green)
              <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{t('projectsList.progress')}</span>
                  <span>{project.progress}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${project.progress}%` }}
                  />
                </div>
              </div>
            ) : null}

            {/* Budget & Tax */}
            <div className="flex justify-between items-end mt-2">
            {canEditSensitive && project.budget && (
                <div className="text-sm text-yellow-400 font-semibold ml-auto">
                💰 {getCurrencySymbol((project as any).currency)}{project.budget.toLocaleString()}
              </div>
            )}
            </div>
          </div>
        </div>
          );
        })}
    </div>

    {contextMenu && (
      <ContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        items={getContextMenuItems(contextMenu.project)}
        onClose={() => setContextMenu(null)}
      />
    )}

    {generalContextMenu && (
      <ContextMenu
        x={generalContextMenu.x}
        y={generalContextMenu.y}
        items={[
          {
            label: t('contextMenu.createProject'),
            icon: <ICONS.Plus className="w-4 h-4" />,
            onClick: onCreateProject,
          },
        ]}
        onClose={() => setGeneralContextMenu(null)}
      />
    )}

    {/* Info Modal for MagicBox messages */}
    {infoModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setInfoModal(null)}>
        <div 
          className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start space-x-3">
            <ICONS.Info className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">{t('common.info') || 'Info'}</h3>
              <p className="text-gray-300">{infoModal.message}</p>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setInfoModal(null)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              {t('common.close') || 'Close'}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default ProjectList;

